/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  DatesSetArg,
  EventClickArg,
  EventDropArg,
  EventInput,
  ToolbarInput,
  ViewApi,
} from "@fullcalendar/core";
import { DateClickArg } from "@fullcalendar/interaction";
import Box from "components/generals/Box";
import Calendar from "components/generals/Calendar";
import PermissionWrapper from "components/generals/PermissionWrapper";
import useHttp from "hooks/http.hook";
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { getFromISOYYYYMMDDDateFormat } from "utils/date.utils";
import moment from "moment";
import {
  AppointmentInterface,
  ProviderInterface,
  HeaderEnum,
  QueryResultInterface,
  VisitStatusEnum,
} from "medicalsystem-shared";
import { Modal } from "components/generals/Modal";
import AppointmentForm from "components/forms/AppointmentForm";
import { FormActionType, EntityPermissions, PermissionsUser } from "types";
import { CoreAPI_URLs } from "constants/http.const";
import { getRangeDateQuery } from "utils/http.utils";
import { getNewAppointment } from "utils/model.utils";
import { eventAction, useAppDispatch } from "store/store";
import { useTranslation } from "react-i18next";
import { MessageType } from "store/slices/event.slice";
import { OptionColor } from "components/content/AppointmentCalendarContent";
import AppointmentArriveTable from "components/tables/AppointmentArriveTable";
import Button from "components/generals/Button";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserPermissions } from "store/slices/auth.slice";
import { useMaterialUIController } from "context/ThemeContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigationHistory } from "hooks/navigation.hook";
import {ArrAppointmentsActions, selectArrAppointmentsDismissedList} from "store/slices/appointment.slice";
import { use } from "i18next";

/**
 * Properties for the AppointmentCalendarView component.
 * 
 * @interface AppointmentCalendarViewProps
 * @property {Date} startDate - Calendar view start date
 * @property {Date} endDate - Calendar view end date
 * @property {string} [initialView='timeGridWeek'] - Initial calendar view type
 * @property {false | ToolbarInput} [headerToolbar] - Calendar toolbar configuration
 * @property {Function} setStartDate - Callback to update start date
 * @property {Function} setEndDate - Callback to update end date
 * @property {ProviderInterface[]} [filterAppointmentsByDoctors] - Filter by specific doctors
 * @property {OptionColor[]} [optionColors] - Color mapping for appointment statuses
 * @property {Function} [setOpenArrModalFromParent] - Callback to control ARR modal from parent
 */
interface AppointmentCalendarViewProps {
  startDate: Date;
  endDate: Date;
  initialView?: string;
  headerToolbar?: false | ToolbarInput;
  setStartDate: Dispatch<SetStateAction<Date>>;
  setEndDate: Dispatch<SetStateAction<Date>>;
  filterAppointmentsByDoctors?: ProviderInterface[];
  optionColors?: OptionColor[];
  openArrModalFromParent?: boolean;
  setOpenArrModalFromParent?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Get color variant for appointment event.
 * 
 * @param {AppointmentInterface} ap - Appointment data
 * @returns {string} Color variant
 */
const getColor = (ap: AppointmentInterface) => {
  return "info";
};

/**
 * Appointment calendar view component using FullCalendar.
 * Interactive calendar for managing medical appointments with multiple views.
 * 
 * Features:
 * - Multiple calendar views: Day, Week, Month
 * - Drag-and-drop appointment rescheduling
 * - Click date to create new appointment
 * - Click event to view/edit appointment
 * - Appointment arrival modal
 * - Filter by doctor/provider
 * - Color-coded appointment statuses
 * - Permission-based edit/create controls
 * - Real-time appointment updates
 * - Date range navigation
 * - Appointment search and filtering
 * - Read-only mode for checked-out visits
 * - Responsive calendar layout
 * - Dark mode support
 * - i18n translation support
 * - Navigation state restoration
 * 
 * Calendar Views:
 * - timeGridDay: Day view with time slots
 * - timeGridWeek: Week view with time slots
 * - dayGridMonth: Month view with day cells
 * 
 * Appointment Actions:
 * - Create: Click empty date/time slot
 * - View/Edit: Click appointment event
 * - Reschedule: Drag event to new time
 * - Arrive: Mark patient arrival
 * - Cancel: Change appointment status
 * 
 * Status Colors:
 * - Scheduled: Default color
 * - Arrived: Arrival color
 * - In Progress: Active color
 * - Checked Out: Completed color (read-only)
 * - Cancelled: Cancelled color (read-only)
 * - No Show: No show color (read-only)
 * 
 * Filtering:
 * - Filter by specific doctors
 * - Date range filtering
 * - Status filtering
 * - Provider filtering
 * 
 * Drag-and-Drop:
 * - Enable appointment rescheduling
 * - Validate new time slot
 * - Update appointment in database
 * - Refresh calendar view
 * - Handle conflicts
 * 
 * Arrival Flow:
 * 1. View day's appointments
 * 2. Click arrival button
 * 3. Open arrival modal
 * 4. Mark patients as arrived
 * 5. Update appointment status
 * 6. Close modal and refresh
 * 
 * Permissions:
 * - Create appointments
 * - Edit appointments
 * - View appointments
 * - Reschedule appointments
 * - Mark arrivals
 * 
 * Integration:
 * - Fetches appointments from API
 * - Updates on date range change
 * - Filters by selected doctors
 * - Syncs with appointment form
 * - Handles navigation state
 * 
 * @param {AppointmentCalendarViewProps} props - Component properties
 * @returns {JSX.Element} Interactive appointment calendar
 */
const AppointmentCalendarView: FC<AppointmentCalendarViewProps> = ({
  startDate,
  endDate,
  headerToolbar,
  initialView = "timeGridWeek",
  setStartDate,
  setEndDate,
  filterAppointmentsByDoctors,
  optionColors,
  openArrModalFromParent,
  setOpenArrModalFromParent,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const dispatch = useAppDispatch();
  const dismissedList = useSelector(selectArrAppointmentsDismissedList);
   const { t, i18n } = useTranslation();
  const [openAppointmentModal, setOpenAppointmentModal] = useState<boolean>(false);
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentInterface>();
  const [appointmentList, setAppointmentList] = useState<AppointmentInterface[]>([]);
  const [openArrModal, setOpenArrModal] = useState<boolean>(false);
  const [arrAppointments, setArrAppointments] = useState<AppointmentInterface[]>([]);
  const location = useLocation();
  const { getSavedEntityIdFromHistory, clearSavedEntityFromHistory } = useNavigationHistory();
  const permissions = useSelector(selectUserPermissions);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentView, setCurrentView] = useState<string>(initialView);
  const [arrFilter, setArrFilter] = useState<string>("");
  const [arrPage, setArrPage] = useState<number>(1);
  const [arrPageSize, setArrPageSize] = useState<number>(10);
  const [arrPageTotal, setArrPageTotal] = useState<number>(1);
  const [arrRowsTotal, setArrRowsTotal] = useState<number>(0);
  const [isArrModalClosedByUser, setIsArrModalClosedByUser]= useState<boolean>(true);
  const [isDismissedByUser, setIsDismissedByUser]= useState<boolean>(false);
  const readOnlyStatuses = [
    VisitStatusEnum.CHECK_OUT,
    VisitStatusEnum.CANCEL,
    VisitStatusEnum.NO_SHOW,
  ];

  const responsiveHeaderToolbar = headerToolbar || (isSmallMobile ? {
    left: "prev,next",
    center: "title",
    right: "today"
  } : isMobile ? {
    left: "prev,next,today",
    center: "title",
    right: "timeGridDay,timeGridWeek"
  } : {
    left: "prev,next,today",
    center: "title",
    right: "timeGridDay,timeGridWeek,dayGridMonth"
  });

  const responsiveInitialView = isMobile && !headerToolbar ? "timeGridDay" : initialView;
  
  useEffect(() => {
    if (location.state && location.state.showPastAppointmentsModal) {
       setOpenArrModal(true);
    }
  }, [location.state]);
  const userString = localStorage.getItem("user");
  const storedUser = JSON.parse(userString??"");

  useEffect(() => {
    if (!isArrModalClosedByUser) {
      setOpenArrModal(true);
    } else {
      setOpenArrModal(false);
    }
  }, [isArrModalClosedByUser]);

  useEffect(() => {
    if(arrRowsTotal && !openArrModalFromParent)
    {
      setIsArrModalClosedByUser(false);
    }
    sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GET_ALL + getRangeDateQuery(startDate, endDate),
      onGetAppointmentsHandler,
      "GET"
    );
  }, [startDate, endDate, filterAppointmentsByDoctors, optionColors]);

  useEffect(() => {   
    const searchParam = arrFilter ? `search=${encodeURIComponent(arrFilter)}&` : "";
    if(!openArrModalFromParent){sendRequest(
      `${CoreAPI_URLs.APPOINTMENTS_GET_ALL}?${searchParam}visitStatus=ARR&page=${arrPage}&pageSize=${arrPageSize}&endDateTime=${new Date().toISOString()}&dismissAppointmentsId=${dismissedList.map(item => item.appointmentId).join(",")}`,
      onGetArrAppointments,
      "GET"
    );}
  }, [startDate, endDate, filterAppointmentsByDoctors, optionColors, arrPage, arrPageSize, arrFilter, openArrModal]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const appointmentId = params.get("appointmentId");
    
    if (appointmentId) {
      sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        (appointment: AppointmentInterface) => {
          if (appointment) {
            setAppointmentDetails(appointment);
            setOpenAppointmentModal(true);
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }]
      );
    }    
  }, [location.search, sendRequest]);

  useEffect(() => {
    const savedEntityId = getSavedEntityIdFromHistory();
    if (savedEntityId) {
      sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        (appointment: AppointmentInterface) => {
          if (appointment) {
            setAppointmentDetails(appointment);
            setOpenAppointmentModal(true);
            clearSavedEntityFromHistory();
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: savedEntityId }]
      );
    }
    dispatch(ArrAppointmentsActions.clearList());
    sendRequest(
      `${CoreAPI_URLs.APPOINTMENTS_GET_ALL}?visitStatus=ARR&endDateTime=${new Date().toISOString()}&dismissAppointmentsId=${dismissedList.map(item => item.appointmentId).join(",")}`,
      (data: QueryResultInterface<AppointmentInterface>) => {
       if(data.total>0) {
        setOpenArrModal(true); 
       }
      },
      "GET"
    );            
  }, []);
  
  useEffect(()=>{
    const searchParam = arrFilter ? `search=${encodeURIComponent(arrFilter)}&` : "";
    if (openArrModalFromParent) {
      sendRequest(
      `${CoreAPI_URLs.APPOINTMENTS_GET_ALL}?${searchParam}visitStatus=ARR&page=${arrPage}&pageSize=${arrPageSize}&endDateTime=${new Date().toISOString()}`,
       onGetAllArrAppointments,
      "GET"
    ); 
    }
  }, [openArrModalFromParent, arrPage, arrPageSize, arrFilter]);


  const canManageAppointments = useMemo(
    () => permissions.includes(PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT),
    []
  );

  const canCreateAppointments = useMemo(
    () => permissions.includes(PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE),
    []
  );

  useEffect(() => {
    setArrPage(1);
  }, [arrPageSize]);
  const onGetAppointmentsHandler = (data: QueryResultInterface<AppointmentInterface>) => {
    const allAppointments = data.results;
    if (filterAppointmentsByDoctors.length > 0) {
      const doctorIdsSet = new Set(filterAppointmentsByDoctors.map((doctor) => doctor.id));
      const filteredAppointments = allAppointments.filter((appointment) =>
        doctorIdsSet.has(appointment.provider.id)
      );
      setAppointmentList(filteredAppointments);
    } else {
      setAppointmentList(allAppointments);
    }
  };

  const onGetArrAppointments = (data: QueryResultInterface<AppointmentInterface>) => {
    setArrAppointments(data.results);
    setArrRowsTotal(data.total);
    setArrPageTotal(Math.ceil(data.total / arrPageSize));
    const hasDismissedAppointments = (dismissedList && dismissedList.length>0);
    const hasResults = (data.results && data.results.length>0);
    const notAllDismissedAppointmentsPresent =(!(dismissedList.every(({ appointmentId }) => data.results.some((item) => item.id === appointmentId))))
    const shouldShowArrModal = hasDismissedAppointments && hasResults && notAllDismissedAppointmentsPresent && isDismissedByUser && appointmentList;
    if (shouldShowArrModal)
    {
      setIsArrModalClosedByUser(false);
      setIsDismissedByUser(false);
      setOpenArrModal(true);  
    }
  };

  const onGetAllArrAppointments = (data: QueryResultInterface<AppointmentInterface>) => {
    setArrAppointments(data.results);
    setArrRowsTotal(data.total);
    setArrPageTotal(Math.ceil(data.total / arrPageSize));
    setOpenArrModal(true);
  };
  const appointmentEvents = useMemo<EventInput[]>(() => {
    const result: EventInput[] = appointmentList.map((ap) => {
      let patientFullName = "";
      let providerFullName = "";
      let bgColor = getColor(ap);

      if (ap.patient) {
        patientFullName =
          (ap.patient.person ? ap.patient.person.name : "[NO NAME]") +
          " " +
          (ap.patient.person ? ap.patient.person.lastname : "[NO LASTNAME]");
      } else {
        patientFullName = "NO PATIENT";
      }
      if (ap.provider) {
        providerFullName =
          (ap.provider.person ? ap.provider.person.name : "[NO PROVIDER NAME]") +
          " " +
          (ap.provider.person ? ap.provider.person.lastname : "[NO PROVIDER LASTNAME]");

        const colorEntry = optionColors.find((colorObj) => colorObj.id === ap.provider.id);
        if (colorEntry) {
          bgColor = colorEntry.color;
        }
      } else {
        providerFullName = "NO PROVIDER";
      }
      return {
        id: ap.id,
        title: ap.reason,
        start: moment(ap.arrivalDateTime).toDate(),
        end: moment(ap.arrivalDateTime).add(ap.duration, "minutes").toDate(),
        className: getColor(ap),
        backgroundColor: bgColor,
        patient: "P: " + patientFullName,
        doctor: "D: " + providerFullName,
      };
    });

    return result;
  }, [appointmentList]);

  const newAppointment = (eventDay: DateClickArg) => {
    if (!canCreateAppointments) {
      dispatch(
        eventAction.addMessage({
          message: t("appointmentCalendarView.noPermissionToCreateAppointments"),
          code: "403",
          messageType: MessageType.WARN,
          showError: false,
          id: crypto.randomUUID(),
        })
      );
      return;
    }

    if(currentView === "dayGridMonth")
    {
      if (!moment(eventDay.date).isBefore(moment(), 'day')) {
        
        setAppointmentDetails(getNewAppointment());
        setOpenAppointmentModal(true);
      } else {
        dispatch(
          eventAction.addMessage({
            message: t("appointmentCalendarView.appointmentRequestInPast"),
            code: "400",
            messageType: MessageType.WARN,
            showError: false,
            id: crypto.randomUUID(),
          })
        );
      }
    }
    else{
    if (!moment(eventDay.date).isBefore(new Date())) {
      setAppointmentDetails(getNewAppointment(eventDay.date));
      setOpenAppointmentModal(true);
    } else {
      dispatch(
        eventAction.addMessage({
          message: t("appointmentCalendarView.appointmentRequestInPast"),
          code: "400",
          messageType: MessageType.WARN,
          showError: false,
          id: crypto.randomUUID(),
        })
      );
    }
    }
  };

  const editAppointmentHandler = (eventClick: EventClickArg) => {
    const appointment = appointmentList.find((ap) => ap.id === eventClick.event.id);
    sendRequest(CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS, onGetAppointment, "GET", undefined, [
      {
        header: HeaderEnum.RESOURCE_ID,
        value: appointment.id,
      },
    ]);
  };

  const onGetAppointment = (appointment: AppointmentInterface) => {
    if (appointment) {
      setAppointmentDetails(appointment);
      setOpenAppointmentModal(true);
    }
  };

  const endDropEventHandler = (arg: EventDropArg) => {
    if (isMobile) {
      arg.revert();
      dispatch(
        eventAction.addMessage({
          message: t("appointmentCalendarView.dragDropNotAvailableOnMobile") || "Drag and drop is not available on mobile devices. Please use the edit form to reschedule appointments.",
          code: "400",
          messageType: MessageType.INFO,
          showError: false,
          id: crypto.randomUUID(),
        })
      );
      return;
    }

    if (!canManageAppointments) {
      arg.revert();
      dispatch(
        eventAction.addMessage({
          message: t("appointmentCalendarView.noPermissionToMoveAppointments"),
          code: "403",
          messageType: MessageType.WARN,
          showError: false,
          id: crypto.randomUUID(),
        })
      );
      return;
    }

    const currentDate = new Date();
    const newStartDate = arg.event.start;
    const endStartDate = arg.event.end;
    const id = arg.event.id;
    const selectedAppointment = appointmentList.find((app) => app.id === id);

    if (
      newStartDate < currentDate ||
      selectedAppointment.visitStatus === VisitStatusEnum.CANCEL ||
      selectedAppointment.visitStatus === VisitStatusEnum.CHECK_OUT ||
      selectedAppointment.visitStatus === VisitStatusEnum.NO_SHOW ||
      selectedAppointment.visitStatus === VisitStatusEnum.CHECK_IN
    ) {
      arg.revert();
      let messageText = "";
      if(newStartDate < currentDate)
      {
        messageText = t("appointmentCalendarView.cannotMoveAppointmentToPast");
      }
      else if(selectedAppointment.visitStatus === VisitStatusEnum.CHECK_IN){
        messageText = t("appointmentCalendarView.cannotMoveCheckedInAppointment");
      }
      else if(selectedAppointment.visitStatus === VisitStatusEnum.CANCEL){
        messageText = t("appointmentCalendarView.cannotMoveCancelledAppointment");
      }
      else if (selectedAppointment.visitStatus === VisitStatusEnum.CHECK_OUT){
        messageText = t("appointmentCalendarView.cannotMoveCheckedOutAppointment");
      }
      else if (selectedAppointment.visitStatus === VisitStatusEnum.NO_SHOW){
        messageText = t("appointmentCalendarView.cannotMoveNoShowAppointment");
      }
      dispatch(
            eventAction.addMessage({
            message: messageText,
            code: "400",
            messageType: MessageType.WARN,
            showError: false,
            id: crypto.randomUUID(),
        })
      );
      return;
    }

    if (selectedAppointment) {
      sendRequest(CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS, null, "GET", null, [
        { header: HeaderEnum.RESOURCE_ID, value: id },
      ]).then((resp) => {
        if (resp.status === 200) {
          const data = resp.data;
          const duration = moment(endStartDate).diff(moment(newStartDate), "minutes");
          const editedAppointment: AppointmentInterface = {
            ...data,
            arrivalDateTime: newStartDate,
            duration: duration,
            vitals: data.vitals ? data.vitals : undefined,
          };
          sendRequest(
            CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
            null,
            "PUT",
            JSON.stringify(editedAppointment),
            [{ header: HeaderEnum.RESOURCE_ID, value: id }]
          ).then((resp) => {
            if (resp.status === 200) {
              updateListHandler(resp.data);
              dispatch(
                eventAction.addSuccessMessage({ message: t("appointmentForm.successEdited"), id: crypto.randomUUID() })
              );
            }
          });
        }
      });
    } else {
      arg.revert();
    }
  };

  const getRangeType = (view: ViewApi) => {
    const calendarType = view.type;
    let result;

    switch (calendarType) {
      case "dayGridMonth":
        result = "month";
        break;
      case "timeGridWeek":
        result = "week";
        break;
      case "timeGridDay":
        result = "day";
        break;
    }
    return result;
  };

  const updateListHandler = (data: AppointmentInterface, newData?: boolean) => {
    if (newData) {
      const newList = [...appointmentList, data];
      setAppointmentList(newList);
    } else {
      const replacedObjectInList = appointmentList.map((app) => (data.id === app.id ? data : app));
      setAppointmentList(replacedObjectInList);
    }
  };

  const onOpenAppointmentForm = (appointment: AppointmentInterface) => {
    setAppointmentDetails(appointment);
    setOpenAppointmentModal(true);
    setOpenArrModal(false);
  };

  const updateAppointmentStatus = async (id: string, newStatus: VisitStatusEnum) => {
    // Try to find in appointmentList first, then fallback to arrAppointments
    const appointmentToUpdate = appointmentList.find((ap) => ap.id === id) || arrAppointments.find((ap) => ap.id === id);
    
    if (!appointmentToUpdate) {
      console.error("Appointment not found in either appointmentList or arrAppointments");
      return;
    }

    // Create a clean copy without vitals first
    const { vitals, ...appointmentWithoutVitals } = appointmentToUpdate;
    
    const updatedAppointment: AppointmentInterface = {
      ...appointmentWithoutVitals,
      visitStatus: newStatus,
    };

    const resp = await sendRequest(
      CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
      (data: AppointmentInterface) => {
        updateListHandler(data);
        dispatch(eventAction.addSuccessMessage({ message: t("appointmentForm.successEdited"), id: crypto.randomUUID() }));
        setArrAppointments((prev) => {
          const updatedList = prev
            .map((ap) => (ap.id === id ? data : ap))
            .filter(
              (ap) =>
                ap.visitStatus === VisitStatusEnum.CHECK_IN &&
                moment(ap.arrivalDateTime).isBefore(new Date())
            );
          if (updatedList.length === 0) {
            setOpenArrModal(false);
          }
          return updatedList;
        });
        setArrRowsTotal((prevTotal) => prevTotal - 1);
      },
      "PUT",
      JSON.stringify(updatedAppointment),
      [{ header: HeaderEnum.RESOURCE_ID, value: id }]
    );
  };

  const handleCancelArrModal = () => {
    setOpenArrModal(false);
    setIsArrModalClosedByUser(true);
    setOpenArrModalFromParent(false);
    setArrPage(1);
  };

  return (
    <Box>
      <Calendar
        locale={i18n.language}
        dateClick={(day) => {
          newAppointment(day);
        }}
        initialDate={getFromISOYYYYMMDDDateFormat(new Date())}
        timeZone="local"
        initialView={responsiveInitialView}
        comingEvents={appointmentEvents}
        eventClick={editAppointmentHandler}
        headerToolbar={responsiveHeaderToolbar}
        views={{
          dayGridMonth: {
            dayMaxEvents: isMobile ? 2 : 1,
          },
          timeGridWeek: {
            dayMaxEvents: isMobile ? 2 : 3,
          },
          timeGridDay: {
            dayMaxEvents: false,
          },
        }}
        height={isMobile ? "auto" : "100%"}
        contentHeight={isMobile ? "70vh" : "auto"}
        slotLabelInterval={{ minutes: isMobile ? 30 : 15 }}
        slotDuration={isMobile ? "00:30:00" : "00:15:00"}
        slotMinTime={"00:00:00"}
        slotMaxTime={"24:00:00"}
        eventMinHeight={isMobile ? 60 : 45}
        buttonText={{
          today: t("appointmentForm.today"),
          month: t("appointmentForm.month"),
          week: t("appointmentForm.week"),
          day: t("appointmentForm.day"),
          list: t("appointmentForm.list"),  
        }}
        allDayText={t("appointmentForm.allDay")}
        eventStartEditable={!isMobile && canManageAppointments}
        selectable={true}
        editable={!isMobile && canManageAppointments}
        droppable={!isMobile && canManageAppointments}
        eventDrop={(arg: EventDropArg) => endDropEventHandler(arg)}
        datesSet={(dateSet: DatesSetArg) => {
          setCurrentView(dateSet.view.type);
          const rangetype = getRangeType(dateSet.view);
          setStartDate(moment(dateSet.view.activeStart).startOf(rangetype).toDate());
          setEndDate(moment(dateSet.view.activeEnd).endOf(rangetype).toDate());
        }}
      />

      <PermissionWrapper
        requiredPermissions={[PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW]}
      >
        <Modal
          open={openAppointmentModal}
          onClose={() => {
            setOpenAppointmentModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          {appointmentDetails && (
            <AppointmentForm
              model={appointmentDetails}
              onCloseAction={() => setOpenAppointmentModal(false)}
              isModal={true}
              updateListHandler={updateListHandler}
              action={readOnlyStatuses.includes(appointmentDetails.visitStatus)?FormActionType.READ_ONLY:(appointmentDetails.id ? FormActionType.MODIFY : FormActionType.NEW)}
              autoOpenVideochat={location.hash === "#videochat"}
              closeAction={() => {
                setOpenAppointmentModal(false);
              }}
              comesFromBilling={false}
            />
          )}
        </Modal>
      </PermissionWrapper>

      <PermissionWrapper
        requiredPermissions={[PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT]}
      >
        <Modal
          open={openArrModal}
          onClose={() => setOpenArrModal(false)}
          aria-labelledby="arr-appointments-modal-title"
          aria-describedby="arr-appointments-modal-description"
        >
          <Box 
            id="cy-appointmentArrive-content"
            className={`arr-modal__wrapper ${darkMode ? 'dark-mode' : ''} ${isMobile ? 'arr-modal__wrapper--mobile' : ''}`}
           >
            <Box className={`arr-modal__header ${darkMode ? 'dark-mode' : ''} ${isMobile ? 'arr-modal__header--mobile' : ''}`}>
              <h2>
                {t("appointmentCalendarView.arrivedAppointments")}  ({arrRowsTotal})
              </h2>
            </Box>

            <Box className="arr-modal__content">
              <AppointmentArriveTable
               canSearchPlaceholder={t("appointmentCalendarView.search")}
                appointments={arrAppointments}
                onViewDetails={onOpenAppointmentForm}
                onUpdateStatus={updateAppointmentStatus}
                entriesPerPage={{ defaultValue: arrPageSize }}
                setPageState={setArrPage}
                setPageSizeState={setArrPageSize}
                apiPagination={true}
                apiPageTotal={arrPageTotal}
                apiRowsTotal={arrRowsTotal}
                apiPageCurrentIndex={arrPage}
                setModelFilter={setArrFilter}
                isLoading={isLoading}
              />
            </Box>

            <Box className={`arr-modal__footer ${isMobile ? 'arr-modal__footer--mobile' : ''}`}>
              {!openArrModalFromParent && (                             
                <Button
                  name="cy-isDismissedByUser-pastAppointment"
                  type="button"
                  color="primary"
                  disabled={isLoading}
                  fullWidth={isMobile}
                  onClick={() => { 
                    sendRequest(
                      `${CoreAPI_URLs.APPOINTMENTS_GET_ALL}?visitStatus=ARR&endDateTime=${new Date().toISOString()}&dismissAppointmentsId=${dismissedList.map(item => item.appointmentId).join(",")}`,
                      (data: QueryResultInterface<AppointmentInterface>) => {
                        const allArrivedAppointments = data.results;
                        allArrivedAppointments.forEach((appointment) => {
                          dispatch(ArrAppointmentsActions.addList({
                            userId: storedUser.id,
                            appointmentId: appointment.id,
                            dateDismissed: new Date(),
                          }));
                        });
                        setOpenArrModal(false); 
                        setIsDismissedByUser(true);
                        setArrPage(1);
                      },
                      "GET"
                    );            
                  }}
                >
                  {t("appointmentCalendarView.dismiss")}
                </Button>
                )}
              <Button
                name="cy-isArrModalClosedByUser-pastAppointment"
                type="button"
                color="secondary"
                disabled={isLoading}
                onClick={handleCancelArrModal}
                fullWidth={isMobile}
              >
                {t("appointmentCalendarView.cancel")}
              </Button>
            </Box>
          </Box>
        </Modal>
      </PermissionWrapper>
    </Box>
  );
};

export default AppointmentCalendarView;